import { CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ABOUT, SITE } from '../data/content';
import { Helmet } from 'react-helmet-async';

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-20">
    <Helmet>
        <title>Rólam | Peredi Krisztina Logopédus Dunakeszi</title>
        <meta name="description" content="Ismerje meg Peredi Krisztina logopédust! Több mint 20 év tapasztalat, szeretetteljes, személyre szabott logopédiai fejlesztés gyermekeknek és felnőtteknek Dunakeszin." />
      </Helmet>
      <section className="py-12 md:py-20 bg-white" aria-labelledby="about-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <h1
              id="about-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-light text-center mb-10 md:mb-16 text-gray-800"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="about-title"
            >
              Rólam
            </h1>

            {/* Profile + Education Cards */}
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center mb-12 md:mb-16">
              <div className="md:w-1/3 flex justify-center w-full">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-200 to-orange-200 rounded-3xl blur-2xl opacity-50 scale-105" aria-hidden="true" />
                  <img
                    src="/profile.png"
                    alt={`${SITE.personName} profilképe`}
                    className="relative w-48 h-48 sm:w-60 sm:h-60 md:w-64 md:h-64 rounded-3xl object-cover shadow-xl"
                    data-testid="about-profile-image"
                  />
                </div>
              </div>

              <div className="md:w-2/3 w-full">
                <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 list-none p-0 m-0">
                  {ABOUT.educationCards.map((card, i) => {
                    const CardIcon = card.Icon;
                    return (
                      <li
                        key={i}
                        className={`bg-gradient-to-br ${card.gradient} p-5 md:p-6 rounded-2xl text-center shadow-md hover:shadow-lg transition-shadow duration-200`}
                        data-testid={`education-card-${i}`}
                      >
                        <CardIcon className={`w-10 h-10 md:w-12 md:h-12 ${card.iconColor} mx-auto mb-3 md:mb-4`} aria-hidden="true" />
                        <h2 className="font-semibold text-gray-800 mb-2 text-sm md:text-base">{card.title}</h2>
                        <p className="text-xs md:text-sm text-gray-600">{card.desc}</p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* Bio */}
            <section className="mb-12 md:mb-16" aria-labelledby="bio-heading">
              <h2
                id="bio-heading"
                className="text-2xl md:text-3xl font-light text-gray-800 mb-5 md:mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Szakmai háttér
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed text-sm sm:text-base md:text-lg">
                {ABOUT.bioParagraphs.map((p, i) => (
                  <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </section>

            {/* Diagnostic + Therapeutic */}
            <div className="grid md:grid-cols-2 gap-6 md:gap-12 mb-12 md:mb-16">
              <section className="bg-gradient-to-br from-purple-100 via-pink-100 to-orange-100 p-6 md:p-8 rounded-3xl shadow-lg" aria-labelledby="diagnostic-heading">
                <h2
                  id="diagnostic-heading"
                  className="text-xl md:text-2xl font-light text-gray-800 mb-4 md:mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Diagnosztikai munka
                </h2>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  Diagnosztikai munkámban hangsúlyt kap a gyermekek és felnőttek részletes logopédiai vizsgálata, mely a szükséges fejlesztéshez mutat irányt. Minden
                  gyermek és felnőtt egyedi, és a személyre szabott fejlesztési tervének alapja a
                  részletes diagnosztika.
                </p>
              </section>

              <section className="bg-gradient-to-br from-green-100 via-cyan-100 to-blue-100 p-6 md:p-8 rounded-3xl shadow-lg" aria-labelledby="therapy-heading">
                <h2
                  id="therapy-heading"
                  className="text-xl md:text-2xl font-light text-gray-800 mb-4 md:mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Terápiás munka
                </h2>
                <div className="text-sm md:text-base text-gray-700 leading-relaxed">
                  <p className="mb-4">Terápiás munkám során foglalkozom:</p>
                  <ul className="space-y-2 list-none p-0 m-0">
                    {ABOUT.therapyAreas.map((area, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-cyan-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span className="text-sm">{area}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </div>

            {/* CTA */}
            <div className="text-center">
              <Link
                to="/kapcsolat"
                data-testid="about-contact-btn"
                className="inline-flex items-center gap-2 px-7 py-4 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-full font-medium hover:from-pink-600 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 no-underline"
              >
                Vegye fel velem a kapcsolatot
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
